import {
  ChallengedAct,
  DictionaryAutocomplete,
  EsFileUnion,
  Participant,
  UserRole,
} from '@models';
import { ParticipantType } from '@enums';

export function autocompleteLabelMapper<T extends DictionaryAutocomplete>({
  label,
  name,
}: T) {
  if (label) {
    return label;
  } else if (name) {
    return name;
  }

  return '-';
}

autocompleteLabelMapper.displayName = 'autocompleteLabelMapper';

export const dictionaryFieldLabelMapper = (
  props?: DictionaryAutocomplete | DictionaryAutocomplete[] | null
) => {
  if (Array.isArray(props)) {
    return props.map((item) => autocompleteLabelMapper(item)).join('; ');
  }

  if (props) {
    return autocompleteLabelMapper(props);
  }

  return '-';
};

dictionaryFieldLabelMapper.displayName = 'dictionaryFieldLabelMapper';

export function multipleAutocompleteColumnMapper<
  T extends DictionaryAutocomplete
>({ value }: { value: T[] }) {
  return (value || []).map((v) => v.label ?? v.name ?? '-').join(', ');
}

multipleAutocompleteColumnMapper.displayName =
  'multipleAutocompleteColumnMapper';

export function autocompleteColumnMapper<T extends DictionaryAutocomplete>({
  value,
}: {
  value: T;
}) {
  return value?.label ?? value?.name ?? '-';
}

autocompleteColumnMapper.displayName = 'autocompleteColumnMapper';

export function subjectContactColumnMapper({
  value,
}: {
  value: Participant[];
}) {
  return value
    ?.map((participant: Participant) => participant?.contact?.label)
    .join(',');
}

subjectContactColumnMapper.displayName = 'subjectContactColumnMapper';

export function judgeSolversMergedColumnMapper({
  rowValue,
  value,
}: {
  rowValue: EsFileUnion;
  value?: { flat?: string[] };
}) {
  if (value?.flat?.length) {
    return value.flat.join(', ');
  }

  if ('judge' in rowValue) {
    return rowValue.judge?.label ?? '-';
  }

  if ('solverUserRoles' in rowValue) {
    return (
      rowValue.solverUserRoles
        ?.map((solver: UserRole) => solver.label)
        .filter((label): label is string => Boolean(label))
        .join(', ') ?? '-'
    );
  }

  return '-';
}

judgeSolversMergedColumnMapper.displayName = 'judgeSolversMergedColumnMapper';

export function participantsWritersMergedColumMapper({
  value,
}: {
  value?: Participant[];
}) {
  return value?.map((participant) => participant.label ?? '-').join(',');
}

participantsWritersMergedColumMapper.displayName =
  'participantsWritersMergedColumMapper';

export const participantTypeLabelMapper: Record<ParticipantType, string> = {
  PROPOSER: 'Navrhovatel',
  ACCESSORY_PARTICIPANT: 'Vedlejší účastník',
  WRITER: 'Pisatel',
  LEGAL_ADVISER: 'Právní zástupce',
};

type StaticMapperDataItem = {
  id?: string | null;
  name?: string | null;
};

function hasMapperValue(value?: string | null) {
  return value != null && value !== '';
}

function mapJoinValues<T>(
  value: T[] | null | undefined,
  getItemValue: (item: T) => string | null | undefined,
  separator: string
) {
  if (!value?.length) {
    return '-';
  }

  return value
    .map((item) => {
      const itemValue = getItemValue(item);
      return hasMapperValue(itemValue) ? itemValue : '?';
    })
    .join(separator);
}

function mapJoinChallengedActValues<T>(
  value: T[] | null | undefined,
  getItemValue: (item: T) => string | null | undefined,
  separator: string
) {
  if (!value?.length) {
    return '-';
  }

  return value.map(getItemValue).filter(hasMapperValue).join(separator);
}

type ColumnMapperWithData<TArgs, TData> = ((
  args: TArgs
) => string | undefined) & {
  data?: TData;
  displayName?: string;
};

export function challengedActsConcernedAuthorityColumnMapper({
  value,
}: {
  value: ChallengedAct[];
}) {
  return mapJoinChallengedActValues(
    value,
    (act: ChallengedAct) => act.concernedAuthority?.name,
    ','
  );
}

challengedActsConcernedAuthorityColumnMapper.displayName =
  'challengedActsConcernedAuthorityColumnMapper';

export function challengedActsExternalFileNumberColumnMapper({
  value,
}: {
  value: ChallengedAct[];
}) {
  return mapJoinChallengedActValues(
    value,
    (act: ChallengedAct) => act.externalFileNumber,
    ','
  );
}

challengedActsExternalFileNumberColumnMapper.displayName =
  'challengedActsExternalFileNumberColumnMapper';

export function senatesColumnMapper<T extends DictionaryAutocomplete>({
  value,
}: {
  value: T[];
}) {
  return mapJoinValues(value, (item: T) => item.label ?? item.name, ', ');
}

senatesColumnMapper.displayName = 'senatesColumnMapper';

export function defaultForAgendasColumnMapper<T extends StaticMapperDataItem>(
  data: T[]
) {
  const mapper: ColumnMapperWithData<
    { value: Array<string | null | undefined> },
    T[]
  > = ({ value }) =>
    mapJoinValues(
      value,
      (defaultAgenda) =>
        data.find((agenda) => agenda.id === defaultAgenda)?.name,
      ', '
    );

  mapper.displayName = 'defaultForAgendasColumnMapper';
  mapper.data = data;

  return mapper;
}

export function createSubstantiveStructureColumnMapper<
  T extends StaticMapperDataItem
>(data: T[]) {
  const mapper: ColumnMapperWithData<
    { value: string | null | undefined },
    T[]
  > = ({ value }) => {
    if (!hasMapperValue(value)) {
      return '-';
    }

    return data.find((structure) => structure.id === value)?.name ?? '-';
  };

  mapper.displayName = 'substantiveStructureColumnMapper';
  mapper.data = data;

  return mapper;
}

export function substantiveStructureColumnMapper<
  T extends StaticMapperDataItem
>(data: T[]) {
  return createSubstantiveStructureColumnMapper(data);
}
